import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import axiosInstance from "../axiosInstance";

const tabs = [
  { key: "incidents", label: "Incidents", icon: "bi-shield-exclamation" },
  { key: "actions", label: "Actions Taken", icon: "bi-clipboard-check" },
  { key: "followups", label: "Follow-ups", icon: "bi-calendar-check" },
  { key: "categories", label: "Categories", icon: "bi-tags" },
  { key: "reports", label: "Reports", icon: "bi-bar-chart" },
];

const emptyIncident = {
  id: null,
  student: "",
  class_group: "",
  category: "",
  title: "",
  description: "",
  incident_date: new Date().toISOString().slice(0, 10),
  incident_time: "",
  location: "",
  witnesses: "",
  severity: "LOW",
  status: "OPEN",
  assigned_to: "",
  parent_notified: false,
  action_summary: "",
  resolution_notes: "",
};

const emptyAction = {
  id: null,
  incident: "",
  action_type: "VERBAL_WARNING",
  action_date: new Date().toISOString().slice(0, 10),
  details: "",
  follow_up_required: false,
  follow_up_date: "",
};

const emptyFollowUp = {
  id: null,
  incident: "",
  follow_up_date: new Date().toISOString().slice(0, 10),
  notes: "",
  status: "PENDING",
};

const emptyCategory = {
  id: null,
  name: "",
  code: "",
  severity_level: "LOW",
  default_action: "",
  description: "",
  is_active: true,
};

function Discipline() {
  const [activeTab, setActiveTab] = useState("incidents");
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [incidents, setIncidents] = useState([]);
  const [actions, setActions] = useState([]);
  const [followUps, setFollowUps] = useState([]);
  const [categories, setCategories] = useState([]);
  const [students, setStudents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [incidentForm, setIncidentForm] = useState(emptyIncident);
  const [actionForm, setActionForm] = useState(emptyAction);
  const [followUpForm, setFollowUpForm] = useState(emptyFollowUp);
  const [categoryForm, setCategoryForm] = useState(emptyCategory);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const visibleIncidents = useMemo(() => {
    const query = search.trim().toLowerCase();
    return incidents.filter((incident) => {
      const matchesStatus = !statusFilter || incident.status === statusFilter;
      if (!matchesStatus) return false;
      if (!query) return true;
      return [
        incident.student_name,
        incident.student_number,
        incident.title,
        incident.category_name,
        incident.class_group_name,
        incident.severity,
        incident.status,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query));
    });
  }, [incidents, search, statusFilter]);

  const openIncidents = incidents.filter((item) => ["OPEN", "UNDER_REVIEW", "ESCALATED"].includes(item.status));

  const loadAll = async () => {
    try {
      setLoading(true);
      await axiosInstance.post("discipline/bootstrap/");
      const [summaryResponse, incidentsResponse, actionsResponse, followUpsResponse, categoriesResponse, optionsResponse] =
        await Promise.all([
          axiosInstance.get("discipline/summary/"),
          axiosInstance.get("discipline/incidents/"),
          axiosInstance.get("discipline/actions/"),
          axiosInstance.get("discipline/follow-ups/"),
          axiosInstance.get("discipline/categories/"),
          axiosInstance.get("discipline/options/"),
        ]);

      setSummary(summaryResponse.data || {});
      setIncidents(incidentsResponse.data || []);
      setActions(actionsResponse.data || []);
      setFollowUps(followUpsResponse.data || []);
      setCategories(categoriesResponse.data || []);
      setStudents(optionsResponse.data?.students || []);
      setStaff(optionsResponse.data?.staff || []);
      setClassGroups(optionsResponse.data?.class_groups || []);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load discipline module.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const updateStudentClass = (studentId) => {
    const selectedStudent = students.find((student) => String(student.id) === String(studentId));
    setIncidentForm((prev) => ({
      ...prev,
      student: studentId,
      class_group: selectedStudent?.class_group || prev.class_group || "",
    }));
  };

  const saveIncident = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = cleanPayload(incidentForm);
      if (incidentForm.id) {
        await axiosInstance.patch(`discipline/incidents/${incidentForm.id}/`, payload);
        toast.success("Discipline incident updated.");
      } else {
        await axiosInstance.post("discipline/incidents/", payload);
        toast.success("Discipline incident recorded.");
      }
      setIncidentForm(emptyIncident);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save discipline incident.");
    } finally {
      setLoading(false);
    }
  };

  const editIncident = (incident) => {
    setIncidentForm({
      id: incident.id,
      student: incident.student || "",
      class_group: incident.class_group || "",
      category: incident.category || "",
      title: incident.title || "",
      description: incident.description || "",
      incident_date: incident.incident_date || new Date().toISOString().slice(0, 10),
      incident_time: normalizeTime(incident.incident_time),
      location: incident.location || "",
      witnesses: incident.witnesses || "",
      severity: incident.severity || "LOW",
      status: incident.status || "OPEN",
      assigned_to: incident.assigned_to || "",
      parent_notified: Boolean(incident.parent_notified),
      action_summary: incident.action_summary || "",
      resolution_notes: incident.resolution_notes || "",
    });
    setActiveTab("incidents");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const saveAction = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = cleanPayload(actionForm);
      if (actionForm.id) {
        await axiosInstance.patch(`discipline/actions/${actionForm.id}/`, payload);
        toast.success("Discipline action updated.");
      } else {
        await axiosInstance.post("discipline/actions/", payload);
        toast.success("Discipline action saved.");
      }
      setActionForm(emptyAction);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save discipline action.");
    } finally {
      setLoading(false);
    }
  };

  const editAction = (action) => {
    setActionForm({
      id: action.id,
      incident: action.incident || "",
      action_type: action.action_type || "VERBAL_WARNING",
      action_date: action.action_date || new Date().toISOString().slice(0, 10),
      details: action.details || "",
      follow_up_required: Boolean(action.follow_up_required),
      follow_up_date: action.follow_up_date || "",
    });
    setActiveTab("actions");
  };

  const saveFollowUp = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = cleanPayload(followUpForm);
      if (followUpForm.id) {
        await axiosInstance.patch(`discipline/follow-ups/${followUpForm.id}/`, payload);
        toast.success("Follow-up updated.");
      } else {
        await axiosInstance.post("discipline/follow-ups/", payload);
        toast.success("Follow-up saved.");
      }
      setFollowUpForm(emptyFollowUp);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save follow-up.");
    } finally {
      setLoading(false);
    }
  };

  const editFollowUp = (followUp) => {
    setFollowUpForm({
      id: followUp.id,
      incident: followUp.incident || "",
      follow_up_date: followUp.follow_up_date || new Date().toISOString().slice(0, 10),
      notes: followUp.notes || "",
      status: followUp.status || "PENDING",
    });
    setActiveTab("followups");
  };

  const saveCategory = async (event) => {
    event.preventDefault();
    try {
      setLoading(true);
      const payload = cleanPayload(categoryForm);
      if (categoryForm.id) {
        await axiosInstance.patch(`discipline/categories/${categoryForm.id}/`, payload);
        toast.success("Category updated.");
      } else {
        await axiosInstance.post("discipline/categories/", payload);
        toast.success("Category added.");
      }
      setCategoryForm(emptyCategory);
      await loadAll();
    } catch (error) {
      showError(error, "Failed to save category.");
    } finally {
      setLoading(false);
    }
  };

  const editCategory = (category) => {
    setCategoryForm({
      id: category.id,
      name: category.name || "",
      code: category.code || "",
      severity_level: category.severity_level || "LOW",
      default_action: category.default_action || "",
      description: category.description || "",
      is_active: Boolean(category.is_active),
    });
    setActiveTab("categories");
  };

  const deleteRecord = async (url, label) => {
    if (!window.confirm(`Delete ${label}?`)) return;
    try {
      setLoading(true);
      await axiosInstance.delete(url);
      toast.success(`${label} deleted.`);
      await loadAll();
    } catch (error) {
      toast.error(`Could not delete ${label}. It may already be in use.`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-3">
        <div className="card-body">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
            <div>
              <h5 className="fw-bold mb-1">Discipline</h5>
              <p className="text-muted mb-0">
                Record incidents, actions taken, parent notifications, follow-ups, and student discipline history.
              </p>
            </div>
            <button className="btn btn-outline-success" onClick={loadAll} disabled={loading}>
              <i className="bi bi-arrow-clockwise me-2"></i>
              Refresh
            </button>
          </div>

          <div className="row g-2 mt-3">
            <Summary label="Incidents" value={summary.incidents || 0} />
            <Summary label="Open" value={summary.open_incidents || 0} />
            <Summary label="Resolved" value={summary.resolved_incidents || 0} />
            <Summary label="Pending Follow-ups" value={summary.pending_follow_ups || 0} />
          </div>
        </div>
      </div>

      <div className="d-flex flex-wrap gap-2 mb-3">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`btn btn-sm ${activeTab === tab.key ? "btn-success" : "btn-light border"}`}
            onClick={() => setActiveTab(tab.key)}
          >
            <i className={`bi ${tab.icon} me-2`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "incidents" && (
        <SetupCard title={incidentForm.id ? "Edit Discipline Incident" : "Record Discipline Incident"}>
          <form className="row g-3 mb-4" onSubmit={saveIncident}>
            <SelectField label="Student" value={incidentForm.student} onChange={updateStudentClass} options={students.map((student) => [student.id, `${student.full_name} (${student.student_number || "No student no."})`])} />
            <SelectField label="Class Group" value={incidentForm.class_group} onChange={(value) => setIncidentForm((prev) => ({ ...prev, class_group: value }))} options={classGroups.map((item) => [item.id, item.display_name || item.name])} required={false} />
            <SelectField label="Category" value={incidentForm.category} onChange={(value) => setIncidentForm((prev) => ({ ...prev, category: value }))} options={categories.filter((item) => item.is_active).map((item) => [item.id, `${item.name} (${item.severity_label || item.severity_level})`])} required={false} />
            <TextField label="Incident Title" value={incidentForm.title} onChange={(value) => setIncidentForm((prev) => ({ ...prev, title: value }))} placeholder="e.g. Class disruption" required />
            <SelectField label="Severity" value={incidentForm.severity} onChange={(value) => setIncidentForm((prev) => ({ ...prev, severity: value }))} options={severityOptions()} />
            <SelectField label="Status" value={incidentForm.status} onChange={(value) => setIncidentForm((prev) => ({ ...prev, status: value }))} options={statusOptions()} />
            <TextField label="Incident Date" type="date" value={incidentForm.incident_date} onChange={(value) => setIncidentForm((prev) => ({ ...prev, incident_date: value }))} required />
            <TextField label="Incident Time" type="time" value={incidentForm.incident_time || ""} onChange={(value) => setIncidentForm((prev) => ({ ...prev, incident_time: value }))} required={false} />
            <TextField label="Location" value={incidentForm.location || ""} onChange={(value) => setIncidentForm((prev) => ({ ...prev, location: value }))} required={false} />
            <SelectField label="Assigned To" value={incidentForm.assigned_to || ""} onChange={(value) => setIncidentForm((prev) => ({ ...prev, assigned_to: value }))} options={staff.map((item) => [item.id, `${item.full_name} (${item.user_type})`])} required={false} />
            <div className="col-12">
              <label className="form-label fw-semibold">Description</label>
              <textarea className="form-control" rows="3" value={incidentForm.description} onChange={(event) => setIncidentForm((prev) => ({ ...prev, description: event.target.value }))} required />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Witnesses / Notes</label>
              <textarea className="form-control" rows="2" value={incidentForm.witnesses || ""} onChange={(event) => setIncidentForm((prev) => ({ ...prev, witnesses: event.target.value }))} />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">Resolution Notes</label>
              <textarea className="form-control" rows="2" value={incidentForm.resolution_notes || ""} onChange={(event) => setIncidentForm((prev) => ({ ...prev, resolution_notes: event.target.value }))} />
            </div>
            <SwitchField label="Parent / guardian notified" checked={incidentForm.parent_notified} onChange={(value) => setIncidentForm((prev) => ({ ...prev, parent_notified: value }))} />
            <SaveBar loading={loading} label={incidentForm.id ? "Update Incident" : "Record Incident"} onCancel={incidentForm.id ? () => setIncidentForm(emptyIncident) : null} />
          </form>

          <div className="row g-2 mb-3">
            <div className="col-md-8">
              <input className="form-control" placeholder="Search student, incident, category or status..." value={search} onChange={(event) => setSearch(event.target.value)} />
            </div>
            <div className="col-md-4">
              <select className="form-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="">All statuses</option>
                {statusOptions().map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>

          <SimpleTable
            headers={["Student", "Incident", "Severity", "Status", "Parent", "Date", "Actions"]}
            rows={visibleIncidents.map((incident) => [
              <div><div className="fw-semibold">{incident.student_name}</div><small className="text-muted">{incident.student_number || "No student number"} · {incident.class_group_name || "No class"}</small></div>,
              <div><div className="fw-semibold">{incident.title}</div><small className="text-muted">{incident.category_name || "Uncategorised"}</small></div>,
              <SeverityBadge value={incident.severity} label={incident.severity_label} />,
              <StatusBadge value={incident.status} label={incident.status_label} />,
              incident.parent_notified ? <span className="badge text-bg-success">Notified</span> : <span className="badge text-bg-light border">Not yet</span>,
              incident.incident_date,
              <ActionButtons onEdit={() => editIncident(incident)} onDelete={() => deleteRecord(`discipline/incidents/${incident.id}/`, incident.title)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "actions" && (
        <SetupCard title={actionForm.id ? "Edit Action Taken" : "Record Action Taken"}>
          <form className="row g-3 mb-4" onSubmit={saveAction}>
            <SelectField label="Incident" value={actionForm.incident} onChange={(value) => setActionForm((prev) => ({ ...prev, incident: value }))} options={incidents.map((incident) => [incident.id, `${incident.student_name} - ${incident.title}`])} />
            <SelectField label="Action Type" value={actionForm.action_type} onChange={(value) => setActionForm((prev) => ({ ...prev, action_type: value }))} options={actionTypeOptions()} />
            <TextField label="Action Date" type="date" value={actionForm.action_date} onChange={(value) => setActionForm((prev) => ({ ...prev, action_date: value }))} required />
            <SwitchField label="Follow-up required" checked={actionForm.follow_up_required} onChange={(value) => setActionForm((prev) => ({ ...prev, follow_up_required: value }))} />
            <TextField label="Follow-up Date" type="date" value={actionForm.follow_up_date || ""} onChange={(value) => setActionForm((prev) => ({ ...prev, follow_up_date: value }))} required={false} />
            <div className="col-12">
              <label className="form-label fw-semibold">Action Details</label>
              <textarea className="form-control" rows="3" value={actionForm.details} onChange={(event) => setActionForm((prev) => ({ ...prev, details: event.target.value }))} required />
            </div>
            <SaveBar loading={loading} label={actionForm.id ? "Update Action" : "Save Action"} onCancel={actionForm.id ? () => setActionForm(emptyAction) : null} />
          </form>

          <SimpleTable
            headers={["Student", "Incident", "Action", "Date", "Follow-up", "Actions"]}
            rows={actions.map((action) => [
              action.student_name,
              action.incident_title,
              action.action_type_label || action.action_type,
              action.action_date,
              action.follow_up_required ? action.follow_up_date || "Required" : "No",
              <ActionButtons onEdit={() => editAction(action)} onDelete={() => deleteRecord(`discipline/actions/${action.id}/`, action.action_type_label || "action")} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "followups" && (
        <SetupCard title={followUpForm.id ? "Edit Follow-up" : "Record Follow-up"}>
          <form className="row g-3 mb-4" onSubmit={saveFollowUp}>
            <SelectField label="Incident" value={followUpForm.incident} onChange={(value) => setFollowUpForm((prev) => ({ ...prev, incident: value }))} options={openIncidents.map((incident) => [incident.id, `${incident.student_name} - ${incident.title}`])} />
            <TextField label="Follow-up Date" type="date" value={followUpForm.follow_up_date} onChange={(value) => setFollowUpForm((prev) => ({ ...prev, follow_up_date: value }))} required />
            <SelectField label="Status" value={followUpForm.status} onChange={(value) => setFollowUpForm((prev) => ({ ...prev, status: value }))} options={[["PENDING", "Pending"], ["COMPLETED", "Completed"], ["ESCALATED", "Escalated"]]} />
            <div className="col-12">
              <label className="form-label fw-semibold">Follow-up Notes</label>
              <textarea className="form-control" rows="3" value={followUpForm.notes} onChange={(event) => setFollowUpForm((prev) => ({ ...prev, notes: event.target.value }))} required />
            </div>
            <SaveBar loading={loading} label={followUpForm.id ? "Update Follow-up" : "Save Follow-up"} onCancel={followUpForm.id ? () => setFollowUpForm(emptyFollowUp) : null} />
          </form>

          <SimpleTable
            headers={["Student", "Incident", "Date", "Status", "Completed By", "Actions"]}
            rows={followUps.map((followUp) => [
              followUp.student_name,
              followUp.incident_title,
              followUp.follow_up_date,
              <StatusBadge value={followUp.status} label={followUp.status_label} />,
              followUp.completed_by_name || "-",
              <ActionButtons onEdit={() => editFollowUp(followUp)} onDelete={() => deleteRecord(`discipline/follow-ups/${followUp.id}/`, "follow-up")} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "categories" && (
        <SetupCard title={categoryForm.id ? "Edit Discipline Category" : "Discipline Categories"}>
          <form className="row g-3 mb-4" onSubmit={saveCategory}>
            <TextField label="Category Name" value={categoryForm.name} onChange={(value) => setCategoryForm((prev) => ({ ...prev, name: value }))} placeholder="e.g. Late Coming" required />
            <TextField label="Code" value={categoryForm.code} onChange={(value) => setCategoryForm((prev) => ({ ...prev, code: value.toUpperCase() }))} placeholder="e.g. LATE" required />
            <SelectField label="Default Severity" value={categoryForm.severity_level} onChange={(value) => setCategoryForm((prev) => ({ ...prev, severity_level: value }))} options={severityOptions()} />
            <SwitchField label="Active" checked={categoryForm.is_active} onChange={(value) => setCategoryForm((prev) => ({ ...prev, is_active: value }))} />
            <div className="col-md-6"><label className="form-label fw-semibold">Default Action</label><textarea className="form-control" rows="2" value={categoryForm.default_action || ""} onChange={(event) => setCategoryForm((prev) => ({ ...prev, default_action: event.target.value }))} /></div>
            <div className="col-md-6"><label className="form-label fw-semibold">Description</label><textarea className="form-control" rows="2" value={categoryForm.description || ""} onChange={(event) => setCategoryForm((prev) => ({ ...prev, description: event.target.value }))} /></div>
            <SaveBar loading={loading} label={categoryForm.id ? "Update Category" : "Save Category"} onCancel={categoryForm.id ? () => setCategoryForm(emptyCategory) : null} />
          </form>

          <SimpleTable
            headers={["Code", "Category", "Severity", "Default Action", "Status", "Actions"]}
            rows={categories.map((category) => [
              category.code,
              category.name,
              <SeverityBadge value={category.severity_level} label={category.severity_label} />,
              category.default_action || "-",
              category.is_active ? <span className="badge text-bg-success">Active</span> : <span className="badge text-bg-secondary">Inactive</span>,
              <ActionButtons onEdit={() => editCategory(category)} onDelete={() => deleteRecord(`discipline/categories/${category.id}/`, category.name)} />,
            ])}
          />
        </SetupCard>
      )}

      {activeTab === "reports" && (
        <SetupCard title="Discipline Reports">
          <div className="row g-3">
            <div className="col-md-4"><ReportCard icon="bi-shield-exclamation" label="Open incidents" value={summary.open_incidents || 0} helper="Needs attention or review" /></div>
            <div className="col-md-4"><ReportCard icon="bi-exclamation-octagon" label="Serious incidents" value={summary.serious_incidents || 0} helper="High priority conduct matters" /></div>
            <div className="col-md-4"><ReportCard icon="bi-people" label="Parents notified" value={summary.parent_notified || 0} helper="Guardian communication recorded" /></div>
          </div>
          <div className="alert alert-light border mt-3 mb-0">
            Student, parent and teacher dashboards can later reuse this same discipline history endpoint without rebuilding the module.
          </div>
        </SetupCard>
      )}
    </div>
  );
}

function SetupCard({ title, children }) {
  return <div className="card border-0 shadow-sm"><div className="card-body"><h6 className="fw-bold mb-3">{title}</h6>{children}</div></div>;
}

function Summary({ label, value }) {
  return <div className="col-6 col-xl-3"><div className="border rounded-3 p-3 bg-light"><small className="text-muted fw-bold">{label}</small><div className="fs-4 fw-bold" style={{ color: "#125b2d" }}>{value}</div></div></div>;
}

function ReportCard({ icon, label, value, helper }) {
  return <div className="border rounded-3 p-3 h-100"><div className="d-flex justify-content-between align-items-center"><div><small className="text-muted fw-bold">{label}</small><div className="fs-3 fw-bold" style={{ color: "#125b2d" }}>{value}</div><small className="text-muted">{helper}</small></div><i className={`bi ${icon} fs-2 text-success`}></i></div></div>;
}

function TextField({ label, value, onChange, type = "text", placeholder = "", required = true }) {
  return <div className="col-md-4"><label className="form-label fw-semibold">{label}</label><input type={type} className="form-control" value={value || ""} placeholder={placeholder} onChange={(event) => onChange(event.target.value)} required={required} /></div>;
}

function SelectField({ label, value, onChange, options, required = true }) {
  return <div className="col-md-4"><label className="form-label fw-semibold">{label}</label><select className="form-select" value={value || ""} onChange={(event) => onChange(event.target.value)} required={required}><option value="">Select {label}</option>{options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}</select></div>;
}

function SwitchField({ label, checked, onChange }) {
  return <div className="col-md-4 d-flex align-items-end"><label className="form-check form-switch mb-2"><input className="form-check-input" type="checkbox" checked={Boolean(checked)} onChange={(event) => onChange(event.target.checked)} /><span className="form-check-label fw-semibold">{label}</span></label></div>;
}

function SaveBar({ loading, label, onCancel }) {
  return <div className="col-12 d-flex gap-2"><button className="btn btn-success" disabled={loading}><i className="bi bi-check2-circle me-2"></i>{label}</button>{onCancel && <button type="button" className="btn btn-light border" onClick={onCancel}>Cancel</button>}</div>;
}

function SimpleTable({ headers, rows }) {
  return <div className="table-responsive border rounded-3"><table className="table table-sm align-middle mb-0"><thead className="table-light"><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{rows.length === 0 ? <tr><td colSpan={headers.length} className="text-center text-muted py-4">No records found.</td></tr> : rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{cell}</td>)}</tr>)}</tbody></table></div>;
}

function ActionButtons({ onEdit, onDelete }) {
  return <div className="d-flex gap-1"><button type="button" className="btn btn-sm btn-light border" onClick={onEdit}><i className="bi bi-pencil-square text-success"></i></button><button type="button" className="btn btn-sm btn-light border" onClick={onDelete}><i className="bi bi-trash text-danger"></i></button></div>;
}

function SeverityBadge({ value, label }) {
  const classes = { LOW: "text-bg-success", MEDIUM: "text-bg-warning", HIGH: "text-bg-danger", SERIOUS: "text-bg-dark" };
  return <span className={`badge ${classes[value] || "text-bg-secondary"}`}>{label || value}</span>;
}

function StatusBadge({ value, label }) {
  const classes = { DRAFT: "text-bg-light border", OPEN: "text-bg-primary", UNDER_REVIEW: "text-bg-warning", RESOLVED: "text-bg-success", ESCALATED: "text-bg-danger", CANCELLED: "text-bg-secondary", PENDING: "text-bg-warning", COMPLETED: "text-bg-success" };
  return <span className={`badge ${classes[value] || "text-bg-secondary"}`}>{label || value}</span>;
}

function cleanPayload(form) {
  return Object.fromEntries(Object.entries(form).filter(([key, value]) => key !== "id" && value !== "" && value !== null && value !== undefined));
}

function normalizeTime(value) {
  if (!value) return "";
  return String(value).slice(0, 5);
}

function showError(error, fallback) {
  const data = error.response?.data;
  const message = data?.detail || data?.non_field_errors?.[0] || Object.values(data || {})?.flat?.()?.[0] || fallback;
  toast.error(message);
}

function severityOptions() {
  return [["LOW", "Low"], ["MEDIUM", "Medium"], ["HIGH", "High"], ["SERIOUS", "Serious"]];
}

function statusOptions() {
  return [["DRAFT", "Draft"], ["OPEN", "Open"], ["UNDER_REVIEW", "Under Review"], ["RESOLVED", "Resolved"], ["ESCALATED", "Escalated"], ["CANCELLED", "Cancelled"]];
}

function actionTypeOptions() {
  return [["VERBAL_WARNING", "Verbal Warning"], ["WRITTEN_WARNING", "Written Warning"], ["DETENTION", "Detention"], ["PARENT_MEETING", "Parent Meeting"], ["COUNSELLING", "Counselling"], ["COMMUNITY_SERVICE", "Community Service"], ["SUSPENSION", "Suspension"], ["OTHER", "Other"]];
}

export default Discipline;
